import { Injectable } from "@angular/core";
import { PaysafeService } from "@modules/paysafe/services/paysafe.service";
import { Router } from "@angular/router";
import { ApicallService } from "@core-services/apicall.service";
import { Modal, ModalService } from "@core-services/modal.service";
import { PaysafeResult } from "@modules/paysafe/models/paysafe-result";
import { ResponseError } from "bp-common-models-lib";
import { FiatTransaction } from "@models/transaction";
import { filter, take, tap } from "rxjs/operators";
import { FiatDepositModalComponent } from "@modules/main/components/step-summary/fiat-transactions/fiat-deposit-modal/fiat-deposit-modal.component";
import { DepositFailedComponent } from "@modules/main/components/step-summary/success-screens/deposit-end-screens/deposit-failed/deposit-failed.component";
import { DepositSuccessComponent } from "@modules/main/components/step-summary/success-screens/deposit-end-screens/deposit-success/deposit-success.component";
import { DepositErrorComponent } from "@modules/main/components/step-summary/success-screens/deposit-end-screens/deposit-error/deposit-error.component";
import { CardDepositPendingComponent } from "@modules/main/components/step-summary/success-screens/card-deposit-pending/card-deposit-pending.component";
import { TranslationService } from "@core-services/translation.service";
import { UserService } from "@main-services/user.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { DepositExpiredComponent } from "@modules/main/components/step-summary/success-screens/deposit-end-screens/deposit-expired/deposit-expired.component";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";

const ERROR_HEADLINES: Map<string, string> = new Map([
	["fwt.paysafe.credit_category.disabled", "deposit_visa_credit_error_headline"],
	["fwt.paysafe.mastercard.disabled", "deposit_mc_error_headline"]
]);

@Injectable()
export class PaysafeStandardService extends PaysafeService {
	private THREE_DS_LOAD_TIMEOUT: number = 5000;
	private THREE_DS_LOAD_INTERVAL: number = 200;
	private THREE_DS_LOAD_TICK_COUNT: number = this.THREE_DS_LOAD_TIMEOUT / this.THREE_DS_LOAD_INTERVAL;

	public constructor(
		public router: Router,
		public apiCallService: ApicallService,
		public translationService: TranslationService,
		public modalService: ModalService,
		public profileFacade: NewProfileFacade,
		public userService: UserService,
		private fiatWalletService: FiatWalletService
	) {
		super(router, apiCallService, translationService, modalService, profileFacade, userService);
	}

	public displayExpiredScreen(): void {
		this.modalService.popAll();
		this.modalService.create(DepositExpiredComponent).present();
		this.expired.next(false);
	}

	protected async processResponse(cardHolderName: string, result: PaysafeResult): Promise<void> {
		const status: boolean | ResponseError[] = await this.sendConfirmation(cardHolderName, result);
		this.handleConfirmation(status);
	}

	protected displayProcessingScreen(): Modal<CardDepositPendingComponent> {
		this.pendingProcess.next(true);
		const modal = this.modalService.create(
			CardDepositPendingComponent,
			{
				header: `${this.translationService.translate("processing_alt_text")}...`
			},
			{ preventBackdropDismiss: true, preventEscDismiss: true }
		);
		modal.present();
		let timeout = 0;
		const interval = setInterval(() => {
			const element: HTMLElement = document.querySelector(".cardinalOverlay-mask.cardinalOverlay-open");
			if (element) {
				element.style.pointerEvents = "none";
				clearInterval(interval);
			}
			timeout++;
			if (timeout > this.THREE_DS_LOAD_TICK_COUNT) {
				clearInterval(interval);
			}
		}, this.THREE_DS_LOAD_INTERVAL);
		return modal;
	}

	protected returnToFormScreen(): void {
		this.modalService.pop();
	}

	private handleConfirmation(status: boolean | ResponseError[]): void {
		if (status instanceof Array) {
			this.showErrorScreen(status[0].code);
			return;
		}

		this.runVerification();
	}

	private runVerification(): void {
		let counter = 0;
		const interval = setInterval(async () => {
			const depositResult = await this.getPendingTransactionStatus();
			if (this.canShowEndScreen(depositResult)) {
				clearInterval(interval);
				this.showEndScreen(this.allowSuccess(depositResult));
				return;
			}
			counter++;
			if (counter >= this.PAYSAFE_COUNTER) {
				clearInterval(interval);
				this.showSuccessScreen();
				return;
			}
		}, 1500);
	}

	private showEndScreen(canShowSuccess: boolean): void {
		this.userService.getActivities();
		this.fiatWalletService.updateReceived();
		canShowSuccess ? this.showSuccessScreen() : this.showFailedScreen();
	}

	private showErrorScreen(errorCode: string): void {
		this.modalService.popAll();
		const modal: Modal<DepositErrorComponent> = this.modalService.create(DepositErrorComponent, {
			headline: ERROR_HEADLINES.get(errorCode)
		});
		this.registerOnChangeDepositMethod(modal);
		this.resetServiceState();
		modal.present();
	}

	private showFailedScreen(): void {
		this.modalService.popAll();
		const modal = this.modalService.create(DepositFailedComponent, {
			fiatTransaction: this.transaction,
			isSavings: false
		});
		this.registerOnChangeDepositMethod(modal);
		this.resetServiceState();
		modal.present();
	}

	private registerOnChangeDepositMethod(modal: Modal): void {
		const fwt: FiatTransaction = new FiatTransaction();
		fwt.setFiatWalletId(this.transaction.getFiatWalletId());
		modal.onDismiss
			.pipe(
				filter(({ data }) => data === "changeDepositMethod"),
				take(1),
				tap(() => {
					this.modalService.create(FiatDepositModalComponent, { data: { transaction: fwt } }).present();
				})
			)
			.subscribe();
	}

	private showSuccessScreen(): void {
		this.resetServiceState();
		this.modalService.popAll();
		this.modalService.create(DepositSuccessComponent).present();
	}

	public onUserProcessClose(): void {
		this.resetServiceState();
	}
}
